import styled from 'styled-components'
import { SvgTick } from '../SvgImports'
import { useCallback, useEffect, useState } from 'react'
import { useClient } from '../../utils/useClient'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import {
  getMarketLanguageState,
  getMarketSettingsState,
} from '../../store/appSlice'
import {
  getIsLoggedInState,
  getMarketDenomination,
  getIsLoadedTruck,
  getSeriesFrame,
  getSessionInitDataState,
  getStartupData,
  getUserSelectionTimestamp,
  setGarageList,
  setUserSelectionTimestamp,
} from '../../store/sessionDataSlice'
import {
  TdsButton,
  TdsMessage,
  TdsRadioButton,
  TdsSpinner,
  TdsTextField,
} from '@scania/tegel-react'
import {
  ScaniaAdobeDownloadType,
  ScaniaAdobeEventId,
  ScaniaAdobeTrackingClickSaveAsGarageEvent,
  ScaniaAdobeTrackingClickSaveGarageEvent,
  ScaniaAdobeTrackingDownloadImageEvent,
  ScaniaAdobeTrackingDownloadPdfEvent,
  ScaniaAdobeTrackingDownloadQrEvent,
  ScaniaAdobeTrackingDownloadXmlEvent,
  pushAdobeEvent,
} from '../../utils/adobeAnalytics'
import { buildPdfUrl } from '../../utils/UrlBuilders'
import { TextId, useTextsV2 } from '../../utils/useTexts'
import { getTruckInfoState, setTruckInfo } from '../../store/truckInfoSlice'
import {
  FlexImageParameters,
  FlexImageV2RequestParams,
} from '../../api/generated'
import { SESSION_FAILURE } from '../../api/errors'
import { closeAllModals } from '../../store/modalSlice'
import { setGvSidePanelContent } from '../../store/sidePanelSlice'
import { SidePanelContent } from '../../store/types'
import { convertReduxStateToTruckInfo } from '../../App'
import { TestElementTypeId } from '../../types/TestAttributeId'

const ContentRoot = styled.div``

const Header = styled.h2`
  margin-bottom: 16px;
`

const Text = styled.p``

interface CardProps {
  $isSelected: boolean
}

const SaveCard = styled.div<CardProps>`
  border: ${(props) =>
    props.$isSelected
      ? '2px solid var(--tds-information)'
      : '1px solid var(--tds-grey-300)'};
  border-radius: 6px;
  padding: 8px;
  padding-left: 24px;
  margin-bottom: 16px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background-color: var(--tds-grey-50);
      cursor: pointer;
    }
  }
`

const CardSelection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const CardTextWrapper = styled.div``

const CardHeader = styled.div`
  font-size: 16px;
  padding-bottom: 4px;
`

const CardText = styled.div`
  font-size: 12px;
`

const CardContent = styled.div`
  margin-top: 16px;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 32px;
  height: 32px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`

const MessageWrapper = styled.div`
  margin-bottom: 16px;
  margin-top: 16px;
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: end;
  margin-top: 8px;
`

const QrStreamWrapper = styled.div`
  height: 164px;

  img {
    height: 160px;
  }
`

interface SpinnerProps {
  $showSpinner: boolean
}

const SpinnerWrapper = styled.div<SpinnerProps>`
  display: ${(props) => (props.$showSpinner ? '' : 'none')};
  margin: 16px;
  height: 40px;

  svg {
    color: var(--tds-blue-600);
  }
`

enum SaveTypes {
  garage,
  pdf,
  picture,
  qr,
  xml,
}

interface ContentSaveProps {
  handleFatalError: () => void
  handleSessionFailure: () => void
}

export function ContentSave({
  handleFatalError,
  handleSessionFailure,
}: ContentSaveProps): JSX.Element {
  const client = useClient()
  const dispatch = useAppDispatch()
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const sessionInitData = useAppSelector(getSessionInitDataState)
  const startupData = useAppSelector(getStartupData)
  const sessionId = sessionInitData?.sessionId
  const t2 = useTextsV2()
  const isLoggedIn = useAppSelector(getIsLoggedInState)
  const marketDenomination = useAppSelector(getMarketDenomination)
  const truckInfo = useAppSelector(getTruckInfoState)
  const marketSettings = useAppSelector(getMarketSettingsState)
  const isLoadedTruck = useAppSelector(getIsLoadedTruck)
  const garageEnabled =
    !truckInfo?.isFactoryModel && marketSettings?.garageEnabled
  const truckInfoDate = convertReduxStateToTruckInfo(truckInfo)
  const userSelectionTimestamp = useAppSelector(getUserSelectionTimestamp)
  const saveEnabled =
    isLoadedTruck &&
    userSelectionTimestamp?.getTime() !== truckInfoDate?.timeLoaded.getTime()
  const { seriesId: imageSeries, frameId: imageFrame } =
    useAppSelector(getSeriesFrame)

  const [selectedSaveType, setSelectedSaveType] = useState<SaveTypes | null>(
    null,
  )
  const [pdfLink, setPdfLink] = useState<string | null>(null)

  const [imagePngUrl, setImagePngUrl] = useState<string | null>(null)
  const [imageJpgUrl, setImageJpgUrl] = useState<string | null>(null)
  const [imageChoosenFormat, setImageChoosenFormat] = useState<string>('png')
  const [imageDownloadName, setImageDownloadName] = useState<string | null>(
    null,
  )

  const FORMAT_PNG = 'Png'
  const FORMAT_SVG = 'Svg'
  const [qrPngDownloadUrl, setQrPngDownloadUrl] = useState<string | null>(null)
  const [qrSvgDownloadUrl, setQrSvgDownloadUrl] = useState<string | null>(null)
  const [qrPngStreamUrl, setQrPngStreamUrl] = useState<string | null>(null)
  const [qrChoosenFormat, setQrChoosenFormat] = useState<string>(FORMAT_PNG)
  const [xmlDownloadUrl, setXmlDownloadUrl] = useState<string | null>(null)
  const [truckName, setTruckName] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleSelectSaveClick = (saveType: SaveTypes) => {
    setSelectedSaveType(saveType)
    setErrorMessage(null)
  }

  // Build PDF-url
  useEffect(() => {
    if (!sessionInitData || !marketLanguage || !startupData) {
      return
    }
    const newLink = buildPdfUrl(
      startupData.backendUrl,
      sessionInitData.sessionId,
      marketLanguage.language,
      truckInfo?.factoryModelName || marketDenomination,
    )
    setPdfLink(newLink)
  }, [
    marketLanguage,
    sessionInitData,
    startupData,
    marketDenomination,
    truckInfo,
  ])

  const handlePdfDownloadButtonClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (!pdfLink) {
        return
      }
      const trackingEvent: ScaniaAdobeTrackingDownloadPdfEvent = {
        event: ScaniaAdobeEventId.Download,
        eventInfo: {
          downloadType: ScaniaAdobeDownloadType.Pdf,
          filePath: pdfLink,
        },
      }
      pushAdobeEvent(trackingEvent)
      window.open(pdfLink, '_blank', 'noreferrer')
      setSelectedSaveType(null)
    },
    [pdfLink],
  )

  useEffect(() => {
    const newName =
      (truckInfo?.savedAsName || marketDenomination) + '.' + imageChoosenFormat
    setImageDownloadName(newName)
  }, [imageChoosenFormat, truckInfo, marketDenomination])

  // Build url for image download
  useEffect(() => {
    async function getImageUrl(imageType: string, bgColorArgb: string) {
      if (!client || !sessionId || !imageSeries || !imageFrame) {
        return
      }
      const flexParams: FlexImageParameters = {
        series: imageSeries,
        frame: imageFrame,
        bgColor: bgColorArgb,
        autoCrop: ['top', 'bottom', 'left', 'right'],
        margin: [40, 40, 40, 40],
        imageType: imageType,
        size: [1280, 1024],
        useBoundingBoxes: false,
      }
      const requestParams: FlexImageV2RequestParams = {
        sessionId,
        flexParams,
        showBodyBuild: false,
      }
      const response = await client.flexImageV2(requestParams)
      if (response.error === SESSION_FAILURE) {
        handleSessionFailure()
        return null
      }
      if (response.error) {
        console.error(response.error)
        handleFatalError()
        return null
      }
      if (!response.success?.url) {
        console.error('Failed to find image URL in response.')
        handleFatalError()
        return null
      }
      return response.success.url
    }
    getImageUrl('png', '#00000000').then((result) => {
      setImagePngUrl(result || null)
    })
    getImageUrl('jpg', '#FFFFFFFF').then((result) => {
      setImageJpgUrl(result || null)
    })
  }, [
    client,
    handleFatalError,
    handleSessionFailure,
    sessionId,
    imageFrame,
    imageSeries,
  ])

  const handlePictureFormatClick = useCallback(
    (format: string) => {
      setImageChoosenFormat(format)
    },
    [setImageChoosenFormat],
  )

  const handleImageSaveClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      const trackingEvent: ScaniaAdobeTrackingDownloadImageEvent = {
        event: ScaniaAdobeEventId.Download,
        eventInfo: {
          downloadType: ScaniaAdobeDownloadType.Image,
          filePath: imageDownloadName || '',
        },
      }
      pushAdobeEvent(trackingEvent)
      setSelectedSaveType(null)
    },
    [imageDownloadName],
  )

  // Build urls for Qr download
  useEffect(() => {
    async function buildQrUrl() {
      if (!marketDenomination || !client || !sessionId) {
        return
      }
      if (!startupData || !marketLanguage) {
        return
      }
      const configAux = {}
      const response = await client.publicConfigV2Save(
        sessionId,
        marketDenomination,
        configAux,
      )
      if (response.error === SESSION_FAILURE) {
        handleSessionFailure()
        return
      }
      if (!response.publicConfigV2Save) {
        throw new Error('Expected response.publicConfigV2Save to be defined.')
      }
      const config = response.publicConfigV2Save
      const pubCfgInfo = config.guid

      const baseQuery =
        '?etel_market=' +
        marketLanguage.market +
        '&etel_language=' +
        marketLanguage.language
      const baseUrl = startupData.backendUrl + '/public_config/QR-' + pubCfgInfo
      const pngStreamUrl = baseUrl + '.png' + baseQuery
      const pngDownloadUrl = pngStreamUrl + '&download=true'
      const svgDownloadUrl = baseUrl + '.svg' + baseQuery + '&download=true'

      return { pngStreamUrl, pngDownloadUrl, svgDownloadUrl }
    }

    buildQrUrl().then((result) => {
      setQrPngDownloadUrl(result?.pngDownloadUrl || null)
      setQrPngStreamUrl(result?.pngStreamUrl || null)
      setQrSvgDownloadUrl(result?.svgDownloadUrl || null)
    })
  }, [
    handleSessionFailure,
    client,
    marketLanguage,
    sessionId,
    startupData,
    marketDenomination,
  ])

  const handleQrFormatClick = (format: string) => {
    setQrChoosenFormat(format)
  }

  const handleQrTracking = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      const url =
        qrChoosenFormat === FORMAT_PNG ? qrPngDownloadUrl : qrSvgDownloadUrl
      const trackingEvent: ScaniaAdobeTrackingDownloadQrEvent = {
        event: ScaniaAdobeEventId.Download,
        eventInfo: {
          downloadType: ScaniaAdobeDownloadType.Qr,
          filePath: url || '',
        },
      }
      pushAdobeEvent(trackingEvent)
      setSelectedSaveType(null)
    },
    [qrChoosenFormat, qrPngDownloadUrl, qrSvgDownloadUrl],
  )

  // Build url for xml download
  useEffect(() => {
    async function buildXmlUrl() {
      if (!sessionId) {
        return
      }
      if (!startupData) {
        return
      }
      const xmlUrl =
        startupData.backendUrl +
        '/api/scania_export_xml?session_id=' +
        sessionId
      setXmlDownloadUrl(xmlUrl)
    }
    buildXmlUrl()
  }, [sessionId, startupData])

  const handleXmlTracking = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      const trackingEvent: ScaniaAdobeTrackingDownloadXmlEvent = {
        event: ScaniaAdobeEventId.Download,
        eventInfo: {
          downloadType: ScaniaAdobeDownloadType.Xml,
          filePath: xmlDownloadUrl || '',
        },
      }
      pushAdobeEvent(trackingEvent)
      setSelectedSaveType(null)
    },
    [xmlDownloadUrl],
  )

  const saveAsNameChangeHandler = (event: any) => {
    const value = event.target.value
    const valueTrimmed = typeof value === 'string' ? value.trim() : null
    if (!valueTrimmed) {
      setTruckName('')
    } else {
      setTruckName(valueTrimmed)
    }
  }

  const handleSaveGarageClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      async function save() {
        if (!truckInfo?.savedAsId) {
          throw new Error('Expected a savedAsId for saved truck.')
        }
        if (!sessionId) {
          throw new Error('Expected sessionId to be defined.')
        }
        if (!client) {
          throw new Error('Expected client to be defined.')
        }
        await client.userConfigV2Save({
          sessionId,
          userConfigId: truckInfo.savedAsId,
          description: truckInfo.savedAsName || '',
        })
        const newTime = new Date()
        if (!truckInfo) {
          dispatch(setTruckInfo(null))
        } else {
          const result = { ...truckInfo }
          result.timeLoaded = newTime.toISOString()
          dispatch(setTruckInfo(result))
        }
        dispatch(setUserSelectionTimestamp(newTime.getTime()))

        const garagelistResponse = await client.userConfigV2List(sessionId)
        if (garagelistResponse.error === SESSION_FAILURE) {
          handleSessionFailure()
          return
        } else if (garagelistResponse.error) {
          handleFatalError()
          return
        }
        const garageList = garagelistResponse.userConfigV2List || []
        dispatch(setGarageList(garageList))
        return
      }
      save()
      dispatch(closeAllModals())

      const trackingEvent: ScaniaAdobeTrackingClickSaveGarageEvent = {
        event: ScaniaAdobeEventId.SaveGarageClick,
      }
      pushAdobeEvent(trackingEvent)
    },
    [
      client,
      dispatch,
      sessionId,
      truckInfo,
      handleFatalError,
      handleSessionFailure,
    ],
  )

  const handleSaveAsGarageClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      async function saveAs() {
        if (!sessionId) {
          return
        }
        if (!client) {
          return
        }
        const response = await client.userConfigV2SaveAs({
          sessionId,
          configName: truckName,
          description: marketDenomination || truckName,
        })
        if (response.error === SESSION_FAILURE) {
          handleSessionFailure()
          return
        }
        if (!response.userConfigV2SaveAs) {
          throw new Error('Expected response.userConfigV2SaveAs to be defined.')
        }
        if (!userSelectionTimestamp) {
          throw new Error('Expected userSelectionTimestamp to be defined.')
        }
        if (!truckInfo) {
          dispatch(setTruckInfo(null))
        } else {
          const result = { ...truckInfo }
          result.timeLoaded = userSelectionTimestamp.toISOString()
          result.savedAsId = response.userConfigV2SaveAs
          result.savedAsName = truckName
          dispatch(setTruckInfo(result))
        }
        const garagelistResponse = await client.userConfigV2List(sessionId)
        if (garagelistResponse.error === SESSION_FAILURE) {
          handleSessionFailure()
          return
        } else if (garagelistResponse.error) {
          handleFatalError()
          return
        }
        const garageList = garagelistResponse.userConfigV2List || []
        dispatch(setGarageList(garageList))
        return
      }
      if (!truckName) {
        setErrorMessage(t2(TextId.ERROR_TRUCK_NAME))
        return
      }

      saveAs()
      dispatch(closeAllModals())
      setSelectedSaveType(null)
      setErrorMessage(null)

      const trackingEvent: ScaniaAdobeTrackingClickSaveAsGarageEvent = {
        event: ScaniaAdobeEventId.SaveAsGarageClick,
      }
      pushAdobeEvent(trackingEvent)
    },
    [
      client,
      t2,
      dispatch,
      handleFatalError,
      handleSessionFailure,
      marketDenomination,
      sessionId,
      truckName,
      userSelectionTimestamp,
      truckInfo,
    ],
  )

  const handleLoginClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    dispatch(setGvSidePanelContent(SidePanelContent.login))
  }

  return (
    <ContentRoot>
      <SpinnerWrapper $showSpinner={false}>
        <TdsSpinner size="sm" />
      </SpinnerWrapper>
      <Header>{t2(TextId.LABEL_ACTION_SAVE)}</Header>
      <Text>{t2(TextId.SIDEPANEL_DESCRIPTION_SAVE)}</Text>
      {garageEnabled && (
        <SaveCard
          $isSelected={selectedSaveType === SaveTypes.garage}
          onClick={() => handleSelectSaveClick(SaveTypes.garage)}
          data-test-element-type={TestElementTypeId.SidePanelSaveGarageCard}
        >
          <CardSelection>
            <CardTextWrapper>
              <CardHeader>{t2(TextId.LABEL_GARAGE)}</CardHeader>
              <CardText>
                {t2(TextId.SIDEPANEL_DESCRIPTION_SAVE_GARAGE)}
              </CardText>
            </CardTextWrapper>
            <IconWrapper>
              {selectedSaveType === SaveTypes.garage && (
                <SvgTick height="24px" />
              )}
            </IconWrapper>
          </CardSelection>
          {selectedSaveType === SaveTypes.garage && isLoggedIn && (
            <CardContent>
              <TdsTextField
                type="text"
                onInput={saveAsNameChangeHandler}
                value={truckName}
                size="md"
                placeholder={t2(TextId.LABEL_TRUCK_NAME)}
                data-test-element-type={
                  TestElementTypeId.SidePanelSaveAsGarageTruckNameInput
                }
              />
            </CardContent>
          )}
          {errorMessage && (
            <MessageWrapper>
              <TdsMessage variant="error" header={errorMessage} />
            </MessageWrapper>
          )}
          {selectedSaveType === SaveTypes.garage && garageEnabled && (
            <ButtonWrapper>
              {isLoggedIn && (
                <div>
                  {isLoadedTruck && (
                    <TdsButton
                      variant="primary"
                      size="md"
                      text={t2(TextId.LABEL_ACTION_SAVE)}
                      onClick={handleSaveGarageClick}
                      disabled={!saveEnabled}
                      fullbleed
                      style={{
                        width: '160px',
                        marginRight: '16px',
                        marginBottom: '8px',
                      }}
                      data-test-element-type={
                        TestElementTypeId.SidePanelSaveGarageSaveButton
                      }
                    />
                  )}
                  <TdsButton
                    variant={isLoadedTruck ? 'secondary' : 'primary'}
                    size="md"
                    text={t2(TextId.LABEL_ACTION_SAVE_AS)}
                    onClick={handleSaveAsGarageClick}
                    fullbleed
                    style={{ width: '160px' }}
                    data-test-element-type={
                      TestElementTypeId.SidePanelSaveAsGarageSaveButton
                    }
                  />
                </div>
              )}
              {!isLoggedIn && (
                <TdsButton
                  variant="primary"
                  size="md"
                  text={t2(TextId.LABEL_ACTION_LOGIN)}
                  onClick={handleLoginClick}
                  fullbleed
                  style={{ width: '160px' }}
                  data-test-element-type={
                    TestElementTypeId.SidePanelSaveGarageLoginButton
                  }
                />
              )}
            </ButtonWrapper>
          )}
        </SaveCard>
      )}
      <SaveCard
        $isSelected={selectedSaveType === SaveTypes.pdf}
        onClick={() => handleSelectSaveClick(SaveTypes.pdf)}
        data-test-element-type={TestElementTypeId.SidePanelSavePdfCard}
      >
        <CardSelection>
          <CardTextWrapper>
            <CardHeader>{t2(TextId.LABEL_ACTION_PDF)}</CardHeader>
            <CardText>{t2(TextId.SIDEPANEL_DESCRIPTION_SAVE_PDF)}</CardText>
          </CardTextWrapper>
          <IconWrapper>
            {selectedSaveType === SaveTypes.pdf && <SvgTick height="24px" />}
          </IconWrapper>
        </CardSelection>
        {selectedSaveType === SaveTypes.pdf && (
          <ButtonWrapper>
            <TdsButton
              variant="primary"
              size="md"
              text={t2(TextId.LABEL_ACTION_SAVE)}
              onClick={handlePdfDownloadButtonClick}
              disabled={pdfLink ? false : true}
              fullbleed
              style={{ width: '160px' }}
              data-test-element-type={
                TestElementTypeId.SidePanelSavePdfDownloadButton
              }
            />
          </ButtonWrapper>
        )}
      </SaveCard>
      <SaveCard
        $isSelected={selectedSaveType === SaveTypes.picture}
        onClick={() => handleSelectSaveClick(SaveTypes.picture)}
        data-test-element-type={TestElementTypeId.SidePanelSaveImageCard}
      >
        <CardSelection>
          <CardTextWrapper>
            <CardHeader>{t2(TextId.SIDEPANEL_SUBHEADER_PICTURE)}</CardHeader>
            <CardText>{t2(TextId.SIDEPANEL_DESCRIPTION_SAVE_IMAGE)}</CardText>
          </CardTextWrapper>
          <IconWrapper>
            {selectedSaveType === SaveTypes.picture && (
              <SvgTick height="24px" />
            )}
          </IconWrapper>
        </CardSelection>
        {selectedSaveType === SaveTypes.picture && (
          <CardContent>
            <TdsRadioButton
              name="image-radio-button"
              value="png"
              onTdsChange={() => handlePictureFormatClick('png')}
              checked={true}
              data-test-element-type={
                TestElementTypeId.SidePanelSaveImagePngRadioButton
              }
            >
              <div slot="label">Png</div>
            </TdsRadioButton>
            <TdsRadioButton
              name="image-radio-button"
              value="jpg"
              onTdsChange={() => handlePictureFormatClick('jpg')}
              checked={false}
              data-test-element-type={
                TestElementTypeId.SidePanelSaveImageJpgRadioButton
              }
            >
              <div slot="label">Jpg</div>
            </TdsRadioButton>
          </CardContent>
        )}
        {selectedSaveType === SaveTypes.picture && (
          <a
            href={
              imageJpgUrl && imagePngUrl && imageChoosenFormat === 'png'
                ? imagePngUrl
                : imageJpgUrl || ''
            }
            download={imageDownloadName}
            style={{ textDecoration: 'none' }}
          >
            <ButtonWrapper>
              <TdsButton
                variant="primary"
                size="md"
                text={t2(TextId.LABEL_ACTION_SAVE)}
                disabled={imageJpgUrl && imagePngUrl ? false : true}
                onClick={handleImageSaveClick}
                fullbleed
                style={{ width: '160px' }}
                data-test-element-type={
                  TestElementTypeId.SidePanelSaveImageDownloadButton
                }
              />
            </ButtonWrapper>
          </a>
        )}
      </SaveCard>
      <SaveCard
        $isSelected={selectedSaveType === SaveTypes.qr}
        onClick={() => handleSelectSaveClick(SaveTypes.qr)}
        data-test-element-type={TestElementTypeId.SidePanelSaveQrCard}
      >
        <CardSelection>
          <CardTextWrapper>
            <CardHeader>{t2(TextId.LABEL_QR_CODE)}</CardHeader>
            <CardText>{t2(TextId.SIDEPANEL_DESCRIPTION_SAVE_QR)}</CardText>
          </CardTextWrapper>
          <IconWrapper>
            {selectedSaveType === SaveTypes.qr && <SvgTick height="24px" />}
          </IconWrapper>
        </CardSelection>
        {selectedSaveType === SaveTypes.qr && (
          <CardContent>
            <TdsRadioButton
              name="qr-radio-button"
              value="png"
              onTdsChange={() => handleQrFormatClick(FORMAT_PNG)}
              checked={true}
              data-test-element-type={
                TestElementTypeId.SidePanelSaveQrPngRadioButton
              }
            >
              <span slot="label">Png</span>
            </TdsRadioButton>
            <TdsRadioButton
              name="qr-radio-button"
              value="svg"
              onTdsChange={() => handleQrFormatClick(FORMAT_SVG)}
              data-test-element-type={
                TestElementTypeId.SidePanelSaveQrSvgRadioButton
              }
            >
              <span slot="label">Svg</span>
            </TdsRadioButton>
            <QrStreamWrapper>
              <img src={qrPngStreamUrl || ''} alt="qr-code"></img>
            </QrStreamWrapper>
          </CardContent>
        )}
        {selectedSaveType === SaveTypes.qr && (
          <a
            href={
              qrPngDownloadUrl &&
              qrSvgDownloadUrl &&
              qrChoosenFormat === FORMAT_PNG
                ? qrPngDownloadUrl
                : qrSvgDownloadUrl || ''
            }
            target={'_blank'}
            rel="noreferrer"
            style={{ textDecoration: 'none' }}
          >
            <ButtonWrapper>
              <TdsButton
                variant="primary"
                size="md"
                text={t2(TextId.LABEL_ACTION_SAVE)}
                onClick={handleQrTracking}
                disabled={qrPngDownloadUrl && qrSvgDownloadUrl ? false : true}
                fullbleed
                style={{ width: '160px' }}
                data-test-element-type={
                  TestElementTypeId.SidePanelSaveQrDownloadButton
                }
              />
            </ButtonWrapper>
          </a>
        )}
      </SaveCard>
      <SaveCard
        $isSelected={selectedSaveType === SaveTypes.xml}
        onClick={() => handleSelectSaveClick(SaveTypes.xml)}
        data-test-element-type={TestElementTypeId.SidePanelSaveXmlCard}
      >
        <CardSelection>
          <CardTextWrapper>
            <CardHeader>{t2(TextId.LABEL_ACTION_SAVE_XML)}</CardHeader>
            <CardText>{t2(TextId.SIDEPANEL_DESCRIPTION_SAVE_XML)}</CardText>
          </CardTextWrapper>
          <IconWrapper>
            {selectedSaveType === SaveTypes.xml && <SvgTick height="24px" />}
          </IconWrapper>
        </CardSelection>
        {selectedSaveType === SaveTypes.xml && (
          <a
            href={xmlDownloadUrl || ''}
            target={'_blank'}
            rel="noreferrer"
            style={{ textDecoration: 'none' }}
          >
            <ButtonWrapper>
              <TdsButton
                variant="primary"
                size="md"
                text={t2(TextId.LABEL_ACTION_SAVE)}
                onClick={handleXmlTracking}
                disabled={xmlDownloadUrl ? false : true}
                fullbleed
                style={{ width: '160px' }}
                data-test-element-type={
                  TestElementTypeId.SidePanelSaveXmlDownloadButton
                }
              />
            </ButtonWrapper>
          </a>
        )}
      </SaveCard>
    </ContentRoot>
  )
}
